import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { getPatientQueryOptions } from '@/api/queries/patients';
import { FullPatient } from '@/api/schema/patient';
import { getProcedureQueryOptions } from '@/api/queries/procedures';
import { ErrorComponent } from '@/components/generalError';
import { OriginalImages } from '@/components/procedureDetails/originalImages/originalImages';
import { Procedure } from '@/components/procedureDetails/procedure/procedure';
import { ProcedureHeader } from '@/components/procedures/procedureHeader';
import { BackButton } from '@/components/ui/backButton';
import { ProceduresContext, QueryStateKey } from '@/context/proceduresContext';
import { Tabs } from '@/components/ui/tabs';
import { usePermission } from '@/hooks/usePermission';
import { routes } from '@/types/routes';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { t } from 'i18next';
import { parseAsNumberLiteral, useQueryState } from 'nuqs';
import { PlanningImages } from '@/components/procedureDetails/planningImages/planningImages';

export const Route = createFileRoute('/_app/procedures/$procedureId/')({
  loader: ({ context: { queryClient }, params: { procedureId } }) => {
    return queryClient.ensureQueryData(getProcedureQueryOptions(procedureId));
  },
  errorComponent: ErrorComponent,
  component: RouteComponent,
});

export const TAB_INDEXES = {
  IMAGES: 0,
  PLANNING: 1,
  PROCEDURE: 2,
};

const useTabs = ({
  patient,
  setActiveTab,
}: {
  patient: FullPatient;
  setActiveTab: (index: number) => Promise<URLSearchParams>;
}) => {
  const tabs = [
    {
      index: TAB_INDEXES.IMAGES,
      name: t('tabs.originalImages', { ns: 'procedures' }),
      component: <OriginalImages patient={ patient } />,
    },
    {
      index: TAB_INDEXES.PLANNING,
      name: t('tabs.planningImages', { ns: 'procedures' }),
      component: <PlanningImages setActiveTab={ setActiveTab } />,
    },
    {
      index: TAB_INDEXES.PROCEDURE,
      name: t('tabs.procedure', { ns: 'procedures' }),
      component: <Procedure />,
    },
  ];

  return tabs;
};

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = Route.useParams();
  const { page: proceduresListPage } = useContext(ProceduresContext);
  const { procedureId } = params as { procedureId: string };
  const { data: procedure } = useSuspenseQuery(
    getProcedureQueryOptions(procedureId),
  );
  const { data: patient } = useSuspenseQuery(
    getPatientQueryOptions(procedure.patient.id),
  );

  const [ activeTab, setActiveTab ] = useQueryState(
    'tab',
    parseAsNumberLiteral(Object.values(TAB_INDEXES)).withDefault(
      TAB_INDEXES.IMAGES,
    ),
  );
  const tabs = useTabs({ patient: patient as FullPatient, setActiveTab });

  const permissions = usePermission('procedure.canRead', {
    siteId: patient.site.id,
  });
  if (!permissions.isLoading && !permissions.hasPermissions) {
    void navigate({ to: routes.patients, replace: true });
    return null;
  }

  return (
    <section className="size-full grid grid-rows-[auto_1fr] gap-space-lg">
      <header className="flex flex-col space-y-space-2xs overflow-hidden">
        <BackButton
          linkLabel={ t('routes.procedureList') }
          href={ `${routes.procedures}?${QueryStateKey}=${proceduresListPage}` }
        />
        <ProcedureHeader procedure={ procedure } patient={ patient } />
      </header>
      <Tabs
        tabs={ tabs }
        activeTab={ activeTab }
        setActiveTab={ setActiveTab }
        className="grid grid-rows-[auto_auto_1fr] gap-space-lg [&]:space-y-0"
      />
    </section>
  );
}
